import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { Preferences } from "@capacitor/preferences";
import { BACKEND_URL } from "../lib/config";

const AuthContext = createContext(null);

const TOKEN_KEY = "smartdream_token";
const LOCAL_PROFILE_KEY = "smartdream_local_profile"; // mattresses + extra (no server endpoint yet)
const CACHED_USER_KEY = "smartdream_cached_user"; // last /me response (offline fallback)
const LAST_SYNC_KEY = "smartdream_last_sync_at";
const RESPONSE_CACHE_PREFIX = "smartdream_cache:";

const API = BACKEND_URL;

// ---- Response cache helpers (transparent offline mode) ----
// Solo GET sono cacheate. Ogni response viene salvata sotto la sua URL.
// In caso di network error, l'interceptor restituisce la copia cacheata
// con headers x-from-cache: 1 e x-cached-at: <iso>.

function cacheKeyFor(config) {
  // Includi anche querystring (asse params) per distinguere fetch differenti.
  const params = config.params
    ? "?" + new URLSearchParams(config.params).toString()
    : "";
  return RESPONSE_CACHE_PREFIX + config.url + params;
}

function writeResponseCache(config, data) {
  if (!config?.url || config.method?.toLowerCase() !== "get") return;
  try {
    const k = cacheKeyFor(config);
    const payload = JSON.stringify({ at: new Date().toISOString(), data });
    // Best-effort: localStorage può fallire per quota. In quel caso non
    // serializziamo niente (la prossima fetch riproverà).
    localStorage.setItem(k, payload);
  } catch {
    /* ignore */
  }
}

function readResponseCache(config) {
  if (!config?.url) return null;
  try {
    const k = cacheKeyFor(config);
    const raw = localStorage.getItem(k);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !("data" in parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function isNetworkError(err) {
  // axios: niente response → fallita prima di arrivare al server (DNS, offline,
  // timeout, CORS). Trattiamo anche 502/503/504 come "rete" perché spesso
  // indicano un proxy che ha perso il backend (e non un'auth problem).
  // ECONNABORTED include timeout iOS. ERR_INTERNET_DISCONNECTED arriva come
  // err.message su WKWebView.
  if (!err?.response) return true;
  const s = err.response.status;
  if (s === 0 || s === 502 || s === 503 || s === 504) return true;
  return false;
}

// Axios instance with auto Bearer injection + response cache fallback.
// Timeout 12s: su iOS reale connessioni morte (WiFi senza internet) possono
// pendere all'infinito; meglio fallire e usare la cache.
const api = axios.create({ baseURL: API, timeout: 12000 });
api.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});
api.interceptors.response.use(
  (response) => {
    // Cache successful GET responses
    writeResponseCache(response.config, response.data);
    try {
      localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString());
    } catch {
      /* ignore */
    }
    // Notifica al context che siamo online (event-driven)
    window.dispatchEvent(new CustomEvent("smartdream:online"));
    return response;
  },
  (error) => {
    // Per GET con network error: ritorna cache se presente
    const cfg = error?.config;
    if (cfg && cfg.method?.toLowerCase() === "get" && isNetworkError(error)) {
      const cached = readResponseCache(cfg);
      if (cached) {
        window.dispatchEvent(
          new CustomEvent("smartdream:offline", { detail: { at: cached.at } })
        );
        return Promise.resolve({
          data: cached.data,
          status: 200,
          statusText: "OK (from cache)",
          headers: { "x-from-cache": "1", "x-cached-at": cached.at },
          config: cfg,
          _fromCache: true,
          _cachedAt: cached.at,
        });
      }
      // Niente cache → segnala offline ma lascia bubblare l'errore
      window.dispatchEvent(new CustomEvent("smartdream:offline"));
    }
    return Promise.reject(error);
  }
);

/**
 * Token persistence helpers.
 *
 * Su iOS Capacitor, localStorage VIVE nel WKWebView; in alcune condizioni (es.
 * iOS ripulisce lo storage per app non usate da molto, o se l'utente disabilita
 * il tracking) può essere svuotato. Per essere resilienti scriviamo il token
 * SIA in localStorage (lettura sincrona, veloce) SIA in Capacitor Preferences
 * (storage nativo persistente). Al boot, se localStorage è vuoto ma Preferences
 * ha il token, lo ripristiniamo.
 */
async function persistToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    try {
      await Preferences.set({ key: TOKEN_KEY, value: token });
    } catch {
      /* non-Capacitor (browser) — ignora */
    }
  } else {
    localStorage.removeItem(TOKEN_KEY);
    try {
      await Preferences.remove({ key: TOKEN_KEY });
    } catch {
      /* ignore */
    }
  }
}

async function hydrateTokenFromPersistentStore() {
  // Se localStorage è già popolato, usa quello (faster path)
  if (localStorage.getItem(TOKEN_KEY)) return;
  try {
    const { value } = await Preferences.get({ key: TOKEN_KEY });
    if (value) localStorage.setItem(TOKEN_KEY, value);
  } catch {
    /* non-Capacitor — niente da fare */
  }
}

function readLocalProfile() {
  try {
    const raw = localStorage.getItem(LOCAL_PROFILE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeLocalProfile(patch) {
  const cur = readLocalProfile();
  const next = { ...cur, ...patch };
  const json = JSON.stringify(next);
  localStorage.setItem(LOCAL_PROFILE_KEY, json);
  // Backup nativo (best-effort, async fire-and-forget)
  Preferences.set({ key: LOCAL_PROFILE_KEY, value: json }).catch(() => {});
  return next;
}

async function hydrateProfileFromPersistentStore() {
  if (localStorage.getItem(LOCAL_PROFILE_KEY)) return;
  try {
    const { value } = await Preferences.get({ key: LOCAL_PROFILE_KEY });
    if (value) localStorage.setItem(LOCAL_PROFILE_KEY, value);
  } catch {
    /* ignore */
  }
}

function mergeUser(serverUser, localExtras) {
  // Hardening: la response del backend potrebbe arrivare malformata o vuota
  // (proxy, network glitch su iOS Safari, ecc.). Tratta `serverUser` undefined
  // come oggetto vuoto invece di crashare con "undefined is not an object".
  const u = serverUser || {};
  const extras = localExtras || {};
  // Backend è la fonte di verità per height/weight (persistito in MongoDB).
  // localStorage extras serve solo per le mattresses (non ancora server-side).
  const serverHeight = u.height_cm != null ? String(u.height_cm) : "";
  const serverWeight = u.weight_kg != null ? String(u.weight_kg) : "";
  return {
    ...u,
    name: u.firstName || (u.email || "").split("@")[0] || "",
    // Preferenza server > cache locale > vuoto
    height: serverHeight || extras.height || "",
    weight: serverWeight || extras.weight || "",
    mattresses: extras.mattresses || [],
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine !== false
  );
  const [lastSyncAt, setLastSyncAt] = useState(() => {
    try {
      return localStorage.getItem(LAST_SYNC_KEY) || null;
    } catch {
      return null;
    }
  });

  // Browser online/offline events + custom events da axios interceptor.
  // Non triggerano logout: aggiornano solo il banner.
  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    const onSdOnline = () => {
      setOnline(true);
      try {
        const t = localStorage.getItem(LAST_SYNC_KEY);
        if (t) setLastSyncAt(t);
      } catch {
        /* ignore */
      }
    };
    const onSdOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("smartdream:online", onSdOnline);
    window.addEventListener("smartdream:offline", onSdOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("smartdream:online", onSdOnline);
      window.removeEventListener("smartdream:offline", onSdOffline);
    };
  }, []);

  // On boot: hydrate token from Capacitor Preferences (iOS-resilient) then
  // validate it by calling /me. Su errore di rete NON ripuliamo il token:
  // l'utente resta loggato in modalità offline con la cache di /me.
  useEffect(() => {
    (async () => {
      await hydrateTokenFromPersistentStore();
      await hydrateProfileFromPersistentStore();
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        setReady(true);
        return;
      }
      // Pre-popola UI con user cached (se presente) per evitare schermo bianco
      try {
        const cachedRaw = localStorage.getItem(CACHED_USER_KEY);
        if (cachedRaw) {
          const cachedUser = JSON.parse(cachedRaw);
          const extras = readLocalProfile();
          setUser(mergeUser(cachedUser, extras));
        }
      } catch {
        /* ignore */
      }
      try {
        const r = await api.get("/api/auth/me");
        const extras = readLocalProfile();
        setUser(mergeUser(r.data, extras));
        // Persisti l'ultimo /me canonico per uso offline
        try {
          localStorage.setItem(CACHED_USER_KEY, JSON.stringify(r.data));
        } catch {
          /* ignore */
        }
      } catch (e) {
        // Token davvero invalido (401/403) → logout. Network/server error →
        // resta autenticato con cache.
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          await persistToken(null);
          setUser(null);
          try {
            localStorage.removeItem(CACHED_USER_KEY);
          } catch {
            /* ignore */
          }
        } else {
          // Network/5xx — resta loggato; se non avevamo cached user, mostra
          // almeno l'utente da localProfile (gating in UI come "offline").
          setOnline(false);
        }
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const setSessionFromAuthResponse = async (data) => {
    // Hardening per iOS Safari: alcune response possono arrivare con body
    // mal-parsato (es. content-type misto). Validiamo prima di scrivere stato.
    if (!data || typeof data !== "object" || !data.access_token) {
      throw new Error(
        "Risposta del server non valida. Riprova tra qualche istante."
      );
    }
    await persistToken(data.access_token);
    const extras = readLocalProfile();
    setUser(mergeUser(data.user, extras));
  };

  const login = async ({ email, password }) => {
    if (!email || !password) throw new Error("Compila tutti i campi");
    try {
      const { data } = await api.post("/api/auth/login", { email, password });
      await setSessionFromAuthResponse(data);
      return data.user;
    } catch (e) {
      throw new Error(formatApiError(e));
    }
  };

  const register = async (form) => {
    try {
      const { data } = await api.post("/api/auth/signup", {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password,
        gender: form.gender,
        birthDate: form.birthDate,
        consent_terms: !!form.consent_terms,
        consent_marketing: !!form.consent_marketing,
        consent_analytics: !!form.consent_analytics,
      });
      await setSessionFromAuthResponse(data);
      return data.user;
    } catch (e) {
      throw new Error(formatApiError(e));
    }
  };

  const logout = async () => {
    await persistToken(null);
    setUser(null);
    try {
      localStorage.removeItem(CACHED_USER_KEY);
    } catch {
      /* ignore */
    }
  };

  // updateUser: salva solo extras locali (height/weight) finché backend non li espone
  const updateUser = (patch) => {
    if (!user) return;
    const extras = readLocalProfile();
    const nextExtras = {
      ...extras,
      height: patch.height !== undefined ? patch.height : extras.height,
      weight: patch.weight !== undefined ? patch.weight : extras.weight,
      mattresses: extras.mattresses || [],
    };
    writeLocalProfile(nextExtras);
    setUser({ ...user, ...patch });
  };

  const addMattress = (m) => {
    if (!user) return;
    const extras = readLocalProfile();
    const list = [...(extras.mattresses || []), m];
    writeLocalProfile({ mattresses: list });
    setUser({ ...user, mattresses: list });
  };

  const removeMattress = (id) => {
    if (!user) return;
    const extras = readLocalProfile();
    const list = (extras.mattresses || []).filter((m) => m.id !== id);
    writeLocalProfile({ mattresses: list });
    setUser({ ...user, mattresses: list });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        ready,
        online,
        lastSyncAt,
        login,
        register,
        logout,
        updateUser,
        addMattress,
        removeMattress,
        api, // axios instance Bearer-injected + cache fallback offline
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// FastAPI 422 ritorna detail come array di {msg,...}. Normalizza in stringa.
function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Errore di rete";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" — ");
  }
  if (typeof d === "object" && typeof d.msg === "string") return d.msg;
  return String(d);
}
